import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// 🔐 Change this password before the event!
const ADMIN_PASSWORD = 'Athleon2025!';

export default function Admin() {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const [houses, setHouses] = useState([]);
  const [events, setEvents] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedGender, setSelectedGender] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('');

  // State: results is now an array of arrays (one per position)
  const [results, setResults] = useState([
    [{ position: 1, student_name: '', class_name: '', house_id: '' }], // Position 1
    [{ position: 2, student_name: '', class_name: '', house_id: '' }], // Position 2
    [{ position: 3, student_name: '', class_name: '', house_id: '' }]  // Position 3
  ]);

  const [currentResults, setCurrentResults] = useState({});
  const [message, setMessage] = useState('');

  // Check if already logged in
  useEffect(() => {
    if (localStorage.getItem('athleon_admin') === 'true') {
      setAuthenticated(true);
    }
  }, []);

  // Fetch data after login
  useEffect(() => {
    if (!authenticated) return;

    const fetchData = async () => {
      try {
        const [housesRes, eventsRes] = await Promise.all([
          fetch('/api/houses'),
          fetch('/api/events')
        ]);

        if (!housesRes.ok || !eventsRes.ok) throw new Error('API fetch failed');

        setHouses(await housesRes.json());
        setEvents(await eventsRes.json());
      } catch (err) {
        console.error('Error fetching data:', err);
        setMessage('❌ Failed to load data. Is the backend running?');
      }
    };

    fetchData();
  }, [authenticated]);

  // Fetch existing results when event changes
  useEffect(() => {
    if (!selectedEvent) {
      setCurrentResults({});
      return;
    }

    fetch(`/api/results/${selectedEvent}`)
      .then(res => res.json())
      .then(data => {
        const resultMap = {};
        data.forEach(r => {
          if (!resultMap[r.position]) resultMap[r.position] = [];
          resultMap[r.position].push(r);
        });
        setCurrentResults(resultMap);

        // Pre-fill form if results exist
        const newResults = [
          resultMap[1] || [{ position: 1, student_name: '', class_name: '', house_id: '' }],
          resultMap[2] || [{ position: 2, student_name: '', class_name: '', house_id: '' }],
          resultMap[3] || [{ position: 3, student_name: '', class_name: '', house_id: '' }]
        ];
        setResults(newResults);
      })
      .catch(err => {
        console.error('Error fetching results:', err);
        setCurrentResults({});
      });
  }, [selectedEvent]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      localStorage.setItem('athleon_admin', 'true');
      setError('');
    } else {
      setError('Incorrect password');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('athleon_admin');
    setAuthenticated(false);
    setPassword('');
  };

  const handleChange = (positionIndex, fieldIndex, field, value) => {
    const newResults = [...results];
    newResults[positionIndex][fieldIndex][field] = value;
    setResults(newResults);
  };

  const addField = (positionIndex) => {
    const newResults = [...results];
    newResults[positionIndex].push({
      position: positionIndex + 1,
      student_name: '',
      class_name: '',
      house_id: ''
    });
    setResults(newResults);
  };

  const removeField = (positionIndex, fieldIndex) => {
    if (results[positionIndex].length <= 1) return; // Always keep at least one field
    const newResults = [...results];
    newResults[positionIndex].splice(fieldIndex, 1);
    setResults(newResults);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    if (!selectedEvent) return setMessage('⚠️ Please select an event.');

    // Flatten and filter out empty entries
    const allEntries = results.flat();
    const validEntries = allEntries.filter(r => 
      r.student_name.trim() !== '' && 
      r.class_name.trim() !== '' && 
      r.house_id
    );

    if (validEntries.length === 0) {
      return setMessage('⚠️ Enter at least one student result.');
    }

    try {
      const response = await fetch('/api/results', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_id: parseInt(selectedEvent),
          results: validEntries // ← only valid entries
        })
      });

      if (response.ok) {
        setMessage('✅ Results saved successfully!');
        // Reset form
        setResults([
          [{ position: 1, student_name: '', class_name: '', house_id: '' }],
          [{ position: 2, student_name: '', class_name: '', house_id: '' }],
          [{ position: 3, student_name: '', class_name: '', house_id: '' }]
        ]);
        setSelectedEvent('');
      } else {
        setMessage('❌ Failed to save results.');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setMessage('❌ Network error.');
    }
  };

  // Login screen
  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white p-8 rounded-xl shadow-lg w-full max-w-md"
        >
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
            🔐 Admin Login
          </h2>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              autoFocus
            />
            {error && <p className="text-red-500 text-center mt-2">{error}</p>}
            <button
              type="submit"
              className="w-full mt-4 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
            >
              Login
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Filter events by category and gender
  const filteredEvents = events.filter(ev => {
    const catMatch = !selectedCategory || ev.category === selectedCategory;
    const genderMatch = !selectedGender ||
      (selectedGender === 'Boys' && ev.name.toLowerCase().includes('boys')) ||
      (selectedGender === 'Girls' && ev.name.toLowerCase().includes('girls'));
    return catMatch && genderMatch;
  });

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">🏆 Enter or Update Results</h1>
        <button
          onClick={handleLogout}
          className="text-sm text-gray-600 hover:text-gray-900 underline"
        >
          Logout
        </button>
      </div>

      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-3 rounded mb-6 ${
            message.includes('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {message}
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white p-6 rounded-xl shadow-md"
      >
        <form onSubmit={handleSubmit}>
          {/* Step 1: Category */}
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">1. Select Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setSelectedGender('');
                setSelectedEvent('');
                setCurrentResults({});
              }}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Choose Category --</option>
              <option value="LP Mini">LP Mini</option>
              <option value="LP Kiddies">LP Kiddies</option>
              <option value="UP Kiddies">UP Kiddies</option>
              <option value="HS Boys">HS Boys</option>
              <option value="HS Girls">HS Girls</option>
            </select>
          </div>

          {/* Step 2: Gender */}
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">2. Select Gender</label>
            <select
              value={selectedGender}
              onChange={(e) => {
                setSelectedGender(e.target.value);
                setSelectedEvent('');
                setCurrentResults({});
              }}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={!selectedCategory}
            >
              <option value="">-- Choose Gender --</option>
              <option value="Boys">Boys</option>
              <option value="Girls">Girls</option>
            </select>
          </div>

          {/* Step 3: Event */}
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">3. Select Event</label>
            <select
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={!selectedCategory || !selectedGender}
            >
              <option value="">-- Choose an event --</option>
              {filteredEvents.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.name}
                </option>
              ))}
            </select>
          </div>

          {/* Results Form */}
          {selectedEvent && (
            <>
              <div className="mb-4 text-sm text-gray-600">
                {Object.keys(currentResults).length > 0
                  ? '✏️ Updating existing results. Modify and save.'
                  : '📝 Enter new results below.'}
              </div>

              {[0, 1, 2].map((posIndex) => (
                <div key={posIndex} className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-bold text-lg text-gray-800">
                      🥇🥈🥉 Position {posIndex + 1}
                    </h3>
                    <button
                      type="button"
                      onClick={() => addField(posIndex)}
                      className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded hover:bg-blue-200"
                    >
                      + Add Another
                    </button>
                  </div>

                  {results[posIndex].map((r, fieldIndex) => (
                    <div key={fieldIndex} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Student Name</label>
                        <input
                          type="text"
                          value={r.student_name}
                          onChange={(e) => handleChange(posIndex, fieldIndex, 'student_name', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded"
                          
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Class (e.g., 3, 7)</label>
                        <input
                          type="text"
                          value={r.class_name}
                          onChange={(e) => handleChange(posIndex, fieldIndex, 'class_name', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded"
                          
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">House</label>
                        <select
                          value={r.house_id}
                          onChange={(e) => handleChange(posIndex, fieldIndex, 'house_id', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded"
                          
                        >
                          <option value="">-- Select --</option>
                          {houses.map((h) => (
                            <option key={h.id} value={h.id}>
                              {h.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {results[posIndex].length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeField(posIndex, fieldIndex)}
                          className="mt-2 text-xs text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ))}

              <button
                type="submit"
                className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition"
              >
                {Object.keys(currentResults).length > 0 ? 'Update Results' : 'Save Results'}
              </button>
            </>
          )}
        </form>
      </motion.div>
      <footer style={{
          marginTop: '40px',
          padding: '16px',
          textAlign: 'center',
          backgroundColor: '#f9fafb',
          color: '#6b7280',
          fontSize: '0.875rem',
          borderTop: '1px solid #e5e7eb'
        }}>
          Designed and Developed by{' '}
          <a
            href="https://mcmedia.in"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#3b82f6',
              fontWeight: '600',
              textDecoration: 'none'
            }}
          >
            MCMedia
          </a>
        </footer>
      
    </div>
  );
}